package one.devsbox.www.ui.widgets

import androidx.compose.animation.AnimatedContent
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.GridItemSpan
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.input.TextFieldState
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import compose.assets.Svg
import compose.assets.svg.ImageOutline
import one.devsbox.shared.ads.DeviceType
import one.devsbox.shared.ads.SharedIO
import one.devsbox.www.www_portal.generated.resources.*
import org.jetbrains.compose.resources.stringResource
import one.devsbox.www.provider.LocalPreferencesProvider
import one.devsbox.www.provider.LocalDisplayProvider
import one.devsbox.www.provider.LocalMeProvider
import tk.mallumo.utils.noDiacriticsLower

@Composable
fun DevicesListing(
    devices: List<SharedIO.DeviceSimple>,
    search: TextFieldState,
    totalCount: Int = devices.size,
    loadingMore: Boolean = false,
    onLoadMore: (() -> Unit)? = null,
    enabled: Boolean = true,
    onClick: (SharedIO.DeviceSimple) -> Unit
) {
    val viewMode = LocalPreferencesProvider.current.deviceLayoutType
    val dp = LocalDisplayProvider.current.state
    val filteredDevices = devices.filter {
        val value = search.text.toString().noDiacriticsLower
        if (value.isEmpty()) true
        else it.name.noDiacriticsLower.contains(value)
    }.sortedByDescending { it.id }

    AnimatedContent(targetState = viewMode) { mode ->
        LazyVerticalGrid(
            columns = if (mode == EntryViewMode.Card) {
                GridCells.Adaptive(minSize = 240.dp)
            } else {
                GridCells.Fixed(1)
            },
            modifier = Modifier.fillMaxSize(),
            contentPadding = PaddingValues(bottom = 250.dp),
            horizontalArrangement = Arrangement.spacedBy(16.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            if (mode == EntryViewMode.List) {
                if (!dp.smallScreen) {
                    item(span = { GridItemSpan(maxLineSpan) }) {
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(horizontal = 16.dp, vertical = 8.dp),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Text(
                                text = stringResource(Res.string.ui_devices_listing_header_name),
                                modifier = Modifier.weight(1f),
                                style = MaterialTheme.typography.labelMedium,
                                fontWeight = FontWeight.Bold,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                            Text(
                                text = stringResource(Res.string.ui_devices_listing_header_status),
                                modifier = Modifier.width(120.dp),
                                style = MaterialTheme.typography.labelMedium,
                                fontWeight = FontWeight.Bold,
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                                textAlign = TextAlign.Center
                            )
                            Text(
                                text = stringResource(Res.string.ui_devices_listing_header_campaigns),
                                modifier = Modifier.width(100.dp),
                                style = MaterialTheme.typography.labelMedium,
                                fontWeight = FontWeight.Bold,
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                                textAlign = TextAlign.Center
                            )
                            Text(
                                text = stringResource(Res.string.ui_devices_listing_header_type),
                                modifier = Modifier.width(100.dp),
                                style = MaterialTheme.typography.labelMedium,
                                fontWeight = FontWeight.Bold,
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                                textAlign = TextAlign.Center
                            )
                            Text(
                                text = stringResource(Res.string.ui_devices_listing_header_id),
                                modifier = Modifier.width(100.dp),
                                style = MaterialTheme.typography.labelMedium,
                                fontWeight = FontWeight.Bold,
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                                textAlign = TextAlign.Center
                            )
                        }
                    }
                    item(span = { GridItemSpan(maxLineSpan) }) {
                        HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                    }
                }
            }

            items(filteredDevices) { device ->
                if (mode == EntryViewMode.Card) {
                    DeviceCard(device, enabled) {
                        onClick(device)
                    }
                } else {
                    DeviceListItem(device, enabled) {
                        onClick(device)
                    }
                }
            }

            if (devices.size < totalCount && onLoadMore != null) {
                item(span = { GridItemSpan(maxLineSpan) }) {
                    LoadMoreButton(
                        isLoading = loadingMore,
                        onClick = onLoadMore
                    )
                }
            }
        }
    }
}

@Composable
private fun DeviceListItem(
    device: SharedIO.DeviceSimple,
    enabled: Boolean,
    onClick: () -> Unit
) {
    val dp = LocalDisplayProvider.current.state
    val me = LocalMeProvider.current
    Surface(
        onClick = onClick,
        enabled = enabled,
        modifier = Modifier.fillMaxWidth().graphicsLayer { alpha = if (enabled) 1f else 0.5f },
        color = Color.Transparent,
        shape = RoundedCornerShape(4.dp),
        border = BorderStroke(
            0.5.dp,
            MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.5f)
        )
    ) {
        if (dp.smallScreen) {
            Row(
                modifier = Modifier.fillMaxWidth()
                    .padding(end = 12.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Box(
                    modifier = Modifier
                        .size(64.dp)
                        .background(MaterialTheme.colorScheme.surfaceVariant),
                    contentAlignment = Alignment.Center
                ) {
                    if (device.photoId > 0) {
                        LazyImage(
                            id = device.photoId,
                            modifier = Modifier.fillMaxSize(),
                            contentScale = ContentScale.Fit
                        )
                    } else {
                        Icon(
                            imageVector = Svg.ImageOutline,
                            contentDescription = null,
                            tint = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.4f),
                            modifier = Modifier.size(16.dp)
                        )
                    }
                }

                Spacer(Modifier.width(12.dp))

                // Name & Location (stacked) + Active campaigns count & type + Online Status circle
                Column(modifier = Modifier.weight(1f)) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        // Online indicator dot
                        Box(
                            modifier = Modifier
                                .size(8.dp)
                                .background(
                                    color = if (device.online) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.error,
                                    shape = CircleShape
                                )
                        )
                        Text(
                            text = device.name.uppercase(),
                            fontWeight = FontWeight.Bold,
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurface,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis,
                            modifier = Modifier.weight(1f)
                        )
                    }

                    Spacer(modifier = Modifier.height(4.dp))
                    val typeText =
                        if (DeviceType.INDOOR match device.type) stringResource(Res.string.ui_devices_listing_type_indoor) else stringResource(
                            Res.string.ui_devices_listing_type_outdoor
                        )
                    Row {
                        Text(
                            text = device.nameLocation.uppercase().ifEmpty { "--" },
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis,
                            modifier = Modifier.weight(1F)
                        )
                        Text(
                            text = " • $typeText",
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                    }
                    if (me.info.adminMode) {
                        Text(
                            text = device.nameCompany,
                            style = MaterialTheme.typography.labelMedium,
                            color = MaterialTheme.colorScheme.secondary,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                    }
                }
            }
        } else {
            Row(
                modifier = Modifier.padding(end = 16.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                // Widescreen 16:9 Thumbnail
                Box(
                    modifier = Modifier
                        .size(80.dp)
                        .background(MaterialTheme.colorScheme.surfaceVariant),
                    contentAlignment = Alignment.Center
                ) {
                    if (device.photoId > 0) {
                        LazyImage(
                            id = device.photoId,
                            modifier = Modifier.fillMaxSize(),
                            contentScale = ContentScale.Crop
                        )
                    } else {
                        Icon(
                            imageVector = Svg.ImageOutline,
                            contentDescription = null,
                            tint = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.4f),
                            modifier = Modifier.size(20.dp)
                        )
                    }
                }

                Spacer(Modifier.width(20.dp))

                // Name & Location
                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        text = device.name.uppercase(),
                        fontWeight = FontWeight.Bold,
                        style = MaterialTheme.typography.bodyLarge,
                        color = MaterialTheme.colorScheme.onSurface,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                    Spacer(modifier = Modifier.height(4.dp))
                    Text(
                        text = device.nameLocation.uppercase().ifEmpty { "--" },
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )

                    if (me.info.adminMode) {
                        Text(
                            text = device.nameCompany,
                            style = MaterialTheme.typography.labelMedium,
                            color = MaterialTheme.colorScheme.secondary,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                    }
                }

                // Status Badge
                Box(Modifier.width(120.dp)) {
                    InternetStatusBadge(device.online, Modifier.align(Alignment.Center))
                }

                // Campaigns Count (dynamic look, with primary color highlight)
                Text(
                    text = device.campaigns.toString(),
                    modifier = Modifier.width(100.dp),
                    style = MaterialTheme.typography.bodyMedium,
                    fontWeight = FontWeight.Bold,
                    textAlign = TextAlign.Center
                )

                // Type Label
                Text(
                    text = if (DeviceType.INDOOR match device.type) stringResource(Res.string.ui_devices_listing_type_indoor) else stringResource(
                        Res.string.ui_devices_listing_type_outdoor
                    ),
                    modifier = Modifier.width(100.dp),
                    style = MaterialTheme.typography.bodySmall,
                    fontWeight = FontWeight.Bold,
                    textAlign = TextAlign.Center
                )

                // Device ID (Base 36)
                Text(
                    text = device.id.toString(36).padStart(4, '0').uppercase(),
                    modifier = Modifier.width(100.dp),
                    style = MaterialTheme.typography.bodySmall.copy(
                        fontFamily = FontFamily.Monospace
                    ),
                    fontWeight = FontWeight.Bold,
                    textAlign = TextAlign.Center,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }
    }
}

@Composable
private fun DeviceCard(device: SharedIO.DeviceSimple, enabled: Boolean, onClick: () -> Unit) {
    Card(
        onClick = onClick,
        enabled = enabled,
        modifier = Modifier.fillMaxWidth().graphicsLayer { alpha = if (enabled) 1f else 0.5f },
        shape = RoundedCornerShape(4.dp),
        colors = CardDefaults.cardColors(containerColor = Color.Transparent),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
    ) {
        Column {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .aspectRatio(16f / 9f)
                    .background(MaterialTheme.colorScheme.surfaceVariant),
                contentAlignment = Alignment.Center
            ) {
                if (device.photoId > 0) {
                    LazyImage(
                        id = device.photoId,
                        modifier = Modifier.fillMaxSize(),
                        contentScale = ContentScale.Crop
                    )
                } else {
                    Column(
                        modifier = Modifier.fillMaxSize(),
                        verticalArrangement = Arrangement.Center,
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Icon(
                            imageVector = Svg.ImageOutline,
                            contentDescription = null,
                            tint = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.4f),
                            modifier = Modifier.size(36.dp)
                        )
                        Spacer(modifier = Modifier.height(8.dp))
                        Text(
                            text = stringResource(Res.string.ui_devices_edit_deviceeui_no_image),
                            style = MaterialTheme.typography.labelSmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.6f)
                        )
                    }
                }


                InternetStatusBadge(device.online, modifier = Modifier.align(Alignment.TopEnd))

            }

            Column(modifier = Modifier.padding(16.dp)) {
                Text(
                    text = device.name.uppercase(),
                    fontWeight = FontWeight.Bold,
                    style = MaterialTheme.typography.bodyLarge,
                    color = MaterialTheme.colorScheme.onSurface,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )

                Spacer(modifier = Modifier.height(4.dp))
                Text(
                    text = device.nameLocation.uppercase().ifEmpty { "--" },
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                val me = LocalMeProvider.current
                if (me.info.adminMode) {
                    Text(
                        text = device.nameCompany,
                        style = MaterialTheme.typography.labelMedium,
                        color = MaterialTheme.colorScheme.secondary,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                }
            }
        }
    }
}


@Composable
private fun LoadMoreButton(isLoading: Boolean, onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 32.dp),
        contentAlignment = Alignment.Center
    ) {
        if (isLoading) {
            CircularProgressIndicator(
                modifier = Modifier.size(24.dp),
                color = MaterialTheme.colorScheme.primary
            )
        } else {
            OutlinedButton(
                onClick = onClick,
                shape = RoundedCornerShape(4.dp),
                colors = ButtonDefaults.outlinedButtonColors(
                    contentColor = MaterialTheme.colorScheme.primary
                ),
                border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
                modifier = Modifier.width(200.dp).height(48.dp)
            ) {
                Text(
                    text = stringResource(Res.string.ui_devices_listing_btn_load_more),
                    style = MaterialTheme.typography.labelLarge,
                    fontWeight = FontWeight.Bold
                )
            }
        }
    }
}